import { Card, CardHeader, Box, Typography, Link, Tooltip, Divider } from "@mui/material";
import { ListContextProvider, toggleListItemExpand, useListController, useRecordContext } from "ra-core";
import { FC, useCallback } from "react";
import { Link as RouteLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Datagrid } from "../list/datagrid";
import Scrollbar from "../Scrollbar";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import TextField from "../field/TextField";
import SizeTextField from "../field/SizeTextField";
import { FunctionField } from "../field";
import { FileAuditResponseDto } from "@/gen/api/securefile";
import { RecPatch } from "@/dataProvider/tools";
import { genUrlWithHashid } from "@/utils/hashid";
import { SecureFilesTableSearchProvider, WithSFTSC } from "./SecureFilesTableSearchProvider";
import { FileCataAmbientDataContextProvider } from "./FileCataAmbientDataProvider";
import SecureFilesTableSearchRegion from "./SecureFilesTableSearchRegion";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { useResourceRegister } from "@/utils/resourceRegister";


const SecureFilesTable: FC = () => {
  useResourceRegister({ name: 'secure-files/audit/user-options' })
  useResourceRegister({ name: 'secure-files/machine-options' })
  const listCtrl = useListController({ resource: 'secure-files-dashboard', basePath: '', filter: {}})
  const format = useUserTimeFormat()
  const dispatch = useDispatch();
  const isRowExpandable = useCallback((x:FileAuditResponseDto&RecPatch) => {
    return Boolean(x.error)
  }, [])

  return (
    <ListContextProvider value={listCtrl}>
      <Card>
        {/* <CardHeader title="Latest Operations" />
        <Divider /> */}
        <SecureFilesTableSearchProvider>
          <WithSFTSC render={(states) =>
            <FileCataAmbientDataContextProvider auditUser={true} machine={true} startTime={states.startTime} endTime={states.endTime}>
              <SecureFilesTableSearchRegion />
            </FileCataAmbientDataContextProvider>
          }/>
        </SecureFilesTableSearchProvider>
        <Divider />
        <Scrollbar options={{ suppressScrollY: true }}>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              expand={<RowExpandFileAuditExtra />}
              isRowExpandable={isRowExpandable}
              {...listCtrl}
              sx={{ 
                '& tr td:nth-of-type(4)': { width: 240 },
                '& span.filename': { 
                  maxWidth: 400, display: 'inline-block',
                  '& a': { wordWrap: 'break-word', },
                },
              }}
            >
              <FunctionField source="timeStamp" label="File Operation Time" showTime 
                render={(d?:FileAuditResponseDto&RecPatch) => {
                  if (!d) { return null }
                  return (
                    <Box>
                      <Typography variant="body2">{format(d.timeStamp!, 'Pp')}</Typography>
                      {d.error && 
                        <Link color="error" sx={{ cursor: 'pointer' }}
                          onClick={() => dispatch(toggleListItemExpand('secure-files-dashboard', d.id))}
                        >with error</Link>}
                    </Box>
                  )
                }}
              />
              <FunctionField label="Action" source="action" sortable={false}
                render={(d?:FileAuditResponseDto&RecPatch) => (
                  <Typography component="span" variant="body2"
                    color={theme => 
                        d?.action === 'Delete' ? theme.palette.error.main
                      : d?.action === 'Decrypt' ? theme.palette.warning.main
                      : undefined
                    }
                  >
                    {d?.action}
                  </Typography>
                )}
              />
              <FunctionField label="Name" source="fileName" sortable={false}
                render={(d?:FileAuditResponseDto&RecPatch) => (
                  <Typography component="span" variant="body2" className="filename">
                    <Tooltip title={d?.path ?? ""}>
                      <Link underline="none"
                        component={RouteLink}
                        to={genUrlWithHashid('/secure-files/$0/show', d?.fileId ?? 0, d?.fileName)}
                      >
                        {d?.path && getFileName(d.path)}
                      </Link>
                    </Tooltip>
                  </Typography>
                )}
              />
              <SizeTextField label="Size" source="size" numericSource="size" emptyText="N/A" sortable={false}/>
              <TextField label="User" source="userName" sortable={false}/>
              <TextField label="Computer" source="machine" />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore
          <DefaultPagination {...listCtrl} />}
      </Card>
    </ListContextProvider>
  )
}

export default SecureFilesTable;

const rgx = /^.*[\\/]/
const getFileName = (pathStr: string) => { 
  const d = pathStr.startsWith('/') ? '/' : '\\'
  return pathStr.split(d).pop()
}

const RowExpandFileAuditExtra : FC = () => {
  const d = useRecordContext<FileAuditResponseDto&RecPatch>()
  return (
    <Box role="dialog" sx={{
      mx: -2, my: -0.75,
    }}>
      <Box sx={{ bgcolor: theme => theme.palette.grey[800], px: 9, py: 1, }}>
        <Typography color="#d78484" variant="body2">{d.error}</Typography>
      </Box>
    </Box>
  )
}
